import { type EnergyAudit } from 'interfaces/EnergyAudit.interface';
import { renderFormattedText } from 'utils/renderFormattedText';
import { formatDecimalSeparator } from 'utils/formatters/number/numberUtils';
import RenderBlocks from '../lists/BulletBlockList';
import { type FC } from 'react';

interface EnergyAuditMethodologyTabProps {
  data: EnergyAudit;
}

const EnergyAuditMethodologyTab: FC<EnergyAuditMethodologyTabProps> = ({
  data,
}) => {
  const parseBlocks = (content: string): any[] | null => {
    try {
      const parsed = JSON.parse(content);
      if (parsed && Array.isArray(parsed.blocks)) {
        return parsed.blocks;
      }
    } catch {
      // Silently handle JSON parsing errors
    }
    return null;
  };

  const detailsBlocks = parseBlocks(data.methodology_details);
  const resultsBlocks = parseBlocks(data.methodology_results);

  // If either has blocks, combine and render all blocks
  if (detailsBlocks ?? resultsBlocks) {
    const allBlocks = [...(detailsBlocks ?? []), ...(resultsBlocks ?? [])];
    return <RenderBlocks blocks={allBlocks} />;
  }

  const renderMethodologyDetails = (): React.ReactNode => {
    return data.methodology_details
      ? renderFormattedText(data.methodology_details)
      : 'No calculation details available.';
  };

  const renderMethodologyResults = (): React.ReactNode => {
    return renderFormattedText(data.methodology_results);
  };

  return (
    <div className="flex flex-col gap-xl">
      {/* How is it calculated? */}
      <div className="flex flex-col gap-sm">
        <div className="uppercase text-sm font-bold text-gray-60 leading-tight">
          How is it calculated?
        </div>
        <div className="text-sm leading-snug text-gray-50">
          {renderMethodologyDetails()}
        </div>
      </div>
      <div className="h-px bg-primary/20 w-full" />
      {/* Results */}
      <div className="flex flex-col gap-sm">
        <div className="uppercase text-sm font-bold text-gray-60 leading-tight">
          Results
        </div>
        <div className="text-sm leading-snug text-gray-50">
          {renderMethodologyResults()}
        </div>
      </div>
      <div className="h-px bg-primary/20 w-full" />
      {/* Financial & CO₂ Savings */}
      <div className="flex flex-col gap-sm">
        <div className="uppercase text-sm font-bold text-gray-60 leading-tight">
          Financial Savings
        </div>
        <div className="text-sm leading-snug text-gray-50">
          {data.calculation_details?.savings_per_year && (
            <>
              Estimated annual savings:{' '}
              <span className="font-medium">
                {formatDecimalSeparator({
                  value: Math.abs(data.calculation_details.savings_per_year),
                  decimalScale: 2,
                })}
                kWh
              </span>
              <br />
            </>
          )}
          {data.calculation_details?.savings_euro_per_year && (
            <>
              Estimated cost savings:{' '}
              <span className="font-medium">
                €
                {formatDecimalSeparator({
                  value: Math.abs(
                    data.calculation_details.savings_euro_per_year
                  ),
                  decimalScale: 2,
                })}
              </span>
              <br />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnergyAuditMethodologyTab;
